import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { JitsiMeeting } from '@jitsi/react-sdk';

/* Importing the components from the material-ui library. */
import {
  Alert,
  AppBar,
  Backdrop,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Snackbar,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBox as AccountBoxIcon,
  Add as AddIcon,
  Adjust as AdjustIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  DevicesOther as DevicesOtherIcon,
  DoNotDisturb as DoNotDisturbIcon,
  FilterCenterFocus as FilterCenterFocusIcon,
  Help as HelpIcon,
  OpenInFull as OpenInFullIcon,
  Rotate90DegreesCw as Rotate90DegreesCwIcon,
  Save as SaveIcon,
  ScreenshotMonitor as ScreenshotMonitorIcon,
} from '@mui/icons-material';

/* Importing the functions from the Redux slices. */
import { searchDevice } from '../../features/device/deviceSlice';
import { searchQuestionnaire } from '../../features/questionnaire/questionnaireSlice';

/* Importing components. */
import AutoCompletePatient from '../../components/AutoCompletePatient';
import DeviceSectionTabs from '../../sections/device/Device.section.details';
import EoSCursorSystem from '../../utils/EoS/EoSCursorSystem';
import PatientSectionTabs from '../../sections/Patient/Patient.section.tabs';
import '../../utils/EoS/style.css';

/* Importing the functions from the utils folder. */
import { fDevice } from '../../utils/Device/Device.utils.format';

export default function MeetingEoS() {
  const jitsiURL = window.REACT_APP_JITSI_URL || `${window.location.hostname}:8443`;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const displayContainerRef = useRef(null);
  const jitsiContainerRef = useRef(null);
  const canvasRef = useRef(null);

  const [isJitsiReady, setIsJitsiReady] = useState(false);
  const [api, setApi] = useState(null);
  const [eos, setEos] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [screenshotData, setScreenshotData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showLifelineModules, setShowLifelineModules] = useState(false);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const [showPatientModule, setShowPatientModule] = useState(false);
  const [showDeviceModule, setShowDeviceModule] = useState(false);
  const [deviceID, setDeviceID] = useState(null);
  const [patientID, setPatientID] = useState(null);
  const [isVideoConferenceJoined, setIsVideoConferenceJoined] = useState(false);
  const [pageDevice, setPageDevice] = useState(null);

  const eosGuiContainerStyle = showLifelineModules ? 'half-width' : 'full-width';
  const lifelineContainerStyle = showLifelineModules ? 'half-width' : 'hidden';

  const updateSearchParams = (pane) => {
    const searchParams = new URLSearchParams(location.search);
    if (pane) {
      searchParams.set('details', pane);
      searchParams.delete('tab');
    } else {
      searchParams.delete('details');
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  // Function to toggle the visibility of lifeline modules container
  const handleLifelineModules = (lifelineModule) => {
    setShowLifelineModules(true);

    if (lifelineModule === 'patient') {
      setShowPatientModule(true);
      setShowDeviceModule(false);
      updateSearchParams('patient');
    } else if (lifelineModule === 'device') {
      setShowPatientModule(false);
      setShowDeviceModule(true);
      updateSearchParams('device');
    }

    setOpenSpeedDial(false);
  };

  const handleCloseLifelineModules = () => {
    setShowLifelineModules(false);
    setShowPatientModule(false);
    setShowDeviceModule(false);
    updateSearchParams(null);
  };

  const searchCurrentDevice = useCallback(async () => {
    const resultAction = await dispatch(searchDevice({ query: `device-name=${params.id}` }));

    if (resultAction.meta.requestStatus === 'fulfilled') {
      const { payload } = resultAction;
      const currentDevice = payload?.entry?.[0]?.resource;
      const resourceID = currentDevice?.id;
      setPageDevice(fDevice(currentDevice));
      if (resourceID) {
        setDeviceID(resourceID);
      } else {
        console.error('Device esource ID not found');
      }
    }
  }, [dispatch, params.id]);

  const handleOpenHelpDialog = () => {
    setOpenHelpDialog(true);
    setOpenSpeedDial(false);
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (api) {
      const handleVideoConferenceJoined = () => {
        setIsVideoConferenceJoined(true);
      };

      api.on('videoConferenceJoined', handleVideoConferenceJoined);
      api.on('readyToClose', () => {
        setIsVideoConferenceJoined(false);
        setIsJitsiReady(false);
        navigate('/meeting/new');
      });

      // Cleanup function
      return () => {
        api.off('videoConferenceJoined', handleVideoConferenceJoined);
      };
    }
  }, [api, navigate]);

  useEffect(() => {
    setIsJitsiReady(true);
  }, []);

  useEffect(() => {
    searchCurrentDevice();
  }, [searchCurrentDevice]);

  useEffect(() => {
    if (!api || !jitsiContainerRef.current || !canvasRef.current || pageDevice?.type !== 'Echo on Sight') return;
    const eosCS = new EoSCursorSystem(api, jitsiContainerRef.current, canvasRef.current);

    setEos(eosCS);
  }, [api, jitsiContainerRef, canvasRef, pageDevice?.type]);

  // Render function for Jitsi spinner/loading indicator
  const renderSpinner = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <CircularProgress />
    </Box>
  );

  const captureScreenshot = async () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const video = document.createElement('video');

    try {
      const captureStream = await navigator.mediaDevices.getDisplayMedia();
      video.srcObject = captureStream;
      video.onloadedmetadata = () => {
        video.play(); // Start playing the video to load the frames

        // Introduce a delay before capturing the screenshot
        setTimeout(() => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
          const frame = canvas.toDataURL('image/png');
          setScreenshotData(frame);
          setOpenDialog(true);

          captureStream.getTracks().forEach((track) => track.stop()); // Stop sharing the screen
        }, 500); // Delay of 500 milliseconds, adjust as needed
      };
    } catch (err) {
      console.error(`Error: ${err}`);
    }
  };

  const copyImageToClipboard = async () => {
    try {
      // Convert the base64 string to a Blob
      const response = await fetch(screenshotData);
      const blob = await response.blob();

      // Use the Clipboard API to copy the Blob
      await navigator.clipboard.write([
        // eslint-disable-next-line no-undef
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);

      setSnackbar({ open: true, message: 'Image copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Error copying image:', err);
      setSnackbar({ open: true, message: 'Error copying image', severity: 'error' });
    }
  };

  const saveImage = () => {
    downloadImage(screenshotData);
  };

  const downloadImage = (imageData) => {
    const link = document.createElement('a');
    link.download = `${params.id}-${new Date().toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })}.png`;

    link.href = imageData;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to handle 'Fill up worksheet' button click
  const handleFillWorksheet = async () => {
    try {
      const formURL = 'https://up-sitaw.org/fhir/Questionnaire/echocardiographic-worksheet';
      // Search for the specific questionnaire
      const searchQuery = `url=${formURL}`;
      const resultAction = await dispatch(searchQuestionnaire({ query: searchQuery }));

      if (resultAction.meta.requestStatus === 'fulfilled') {
        const { payload } = resultAction;
        const resourceID = payload?.entry?.[0]?.resource?.id;

        if (resourceID) {
          window.open(`/questionnaireresponse/add/${resourceID}`, '_blank');
        } else {
          console.error('Resource ID not found');
        }
      } else {
        console.error('Error in searching questionnaire:', resultAction.payload.error);
      }
    } catch (error) {
      console.error('Error in searching questionnaire:', error);
    }
  };

  const speedDialActionsDefault = [
    { icon: <ScreenshotMonitorIcon />, name: 'Screenshot', action: captureScreenshot },
    { icon: <AccountBoxIcon />, name: 'Patient', action: () => handleLifelineModules('patient') },
    { icon: <DevicesOtherIcon />, name: 'Device', action: () => handleLifelineModules('device') },
  ];

  const speedDialActionsEoS = [
    { icon: <AddIcon />, name: 'Worksheet', action: handleFillWorksheet },
    { icon: <ScreenshotMonitorIcon />, name: 'Screenshot', action: captureScreenshot },
    { icon: <AccountBoxIcon />, name: 'Patient', action: () => handleLifelineModules('patient') },
    { icon: <DevicesOtherIcon />, name: 'Device', action: () => handleLifelineModules('device') },
    { icon: <HelpIcon />, name: 'Help', action: handleOpenHelpDialog },
  ];

  return (
    <>
      <Helmet>
        <title>{`Meeting ${` ${params.id}`} | Lifeline`}</title>
      </Helmet>
      <div id="container">
        <div id="eos-gui-container">
          <Backdrop open={openSpeedDial} sx={{ zIndex: 1 }} />
          <div id="display-container" ref={displayContainerRef} className={eosGuiContainerStyle}>
            {isJitsiReady && (
              <>
                <div ref={jitsiContainerRef}>
                  <JitsiMeeting
                    roomName={`${params.id}`}
                    spinner={renderSpinner}
                    domain={jitsiURL}
                    getIFrameRef={(iframeRef) => {
                      iframeRef.style.height = pageDevice?.type === 'Echo on Sight' ? '100%': 'calc(100vh - 52px)';
                      iframeRef.style.width = '100%';
                    }}
                    onApiReady={(externalApi) => {
                      setApi(externalApi);
                    }}
                    configOverwrite={{
                      apiLogLevels: ['error'],
                    }}
                  />
                </div>
                <canvas ref={canvasRef} id="canvas" />
              </>
            )}
          </div>

          <div id="lifeline-modules-container" className={lifelineContainerStyle}>
            <Box width="100%" sx={{ p: 1 }}>
              {showDeviceModule && deviceID && <DeviceSectionTabs deviceID={deviceID} call />}
              {showPatientModule && (
                <>
                  {patientID ? (
                    <PatientSectionTabs patientID={patientID} call />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Select a patient record to view
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </div>
        </div>
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          aria-labelledby="screenshot-dialog-title"
          maxWidth="lg"
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', alignItems: 'center' }}>
            <Button onClick={copyImageToClipboard} startIcon={<ContentCopyIcon />} size="small">
              Copy
            </Button>
            <Button onClick={saveImage} startIcon={<SaveIcon />} size="small">
              Save
            </Button>

            <Divider orientation="vertical" flexItem style={{ margin: '0 10px' }} />

            <IconButton onClick={() => setOpenDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <DialogContent>
            {screenshotData && <img src={screenshotData} alt="Screenshot" style={{ maxWidth: '100%' }} />}
          </DialogContent>
        </Dialog>
        <Dialog open={openHelpDialog} onClose={() => setOpenHelpDialog(false)}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', alignItems: 'center' }}>
            <IconButton onClick={() => setOpenHelpDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          <DialogContent>
            <Box sx={{ px: 3 }}>
              <Typography variant="h6" gutterBottom>
                Echo on Sight (EoS) Cursor System
              </Typography>
              <Typography paragraph>
                Welcome to the EoS Cursor System! This system enhances your video conferencing experience by enabling
                annotations within the video feed. Here's how you can utilize this feature:
              </Typography>
              <Typography gutterBottom>
                <ol>
                  <li>
                    <strong>Joining the Meeting:</strong> Upon entering the meeting, the cursor functionality becomes
                    available for use.
                  </li>
                  <li>
                    <strong>Positioning the Start Marker (Cursor 1):</strong>
                    <ul>
                      <li>
                        Click the <strong>Cursor 1</strong> button to activate the start marker.
                      </li>
                      <li>Then, click on the desired position within the video feed to place this marker.</li>
                      <li>You can drag the marker around the video feed to adjust its position as needed.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Positioning the End Marker (Cursor 2):</strong>
                    <ul>
                      <li>
                        Click the <strong>Cursor 2</strong> button to activate the end marker.
                      </li>
                      <li>Similarly, click on the video feed to place the end marker.</li>
                      <li>This marker can also be repositioned by dragging.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Finalizing the Annotation:</strong>
                    <ul>
                      <li>
                        Use the <strong>Drop Cursor</strong> button to lock both markers in place.
                      </li>
                      <li>Once selected, the markers will stay fixed at their designated positions.</li>
                    </ul>
                  </li>
                </ol>
              </Typography>
              <Typography paragraph>
                Click the <strong>Rotate</strong> button to rotate the video feed by 90 degrees. This feature can be
                useful for adjusting the orientation of the video for better viewing and annotation.
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <AppBar
          position="static"
          color="transparent"
          elevation={0}
          sx={{
            boxShadow: 'none',
            backdropFilter: 'blur(6px)',
            webkitBackdropFilter: 'blur(6px)',
            p: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: showLifelineModules ? '50vw' : '100%',
              pr: showLifelineModules ? 1 : 0,
              borderRight: showLifelineModules ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
            }}
          >
            {showLifelineModules ? (
              <Tooltip title="Back">
                <IconButton color="primary" aria-label="back" onClick={() => navigate('/meeting/new')}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate('/meeting/new')}
                sx={{ width: '125px' }}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
            )}

            {pageDevice?.type === 'Echo on Sight' && (
              <Box sx={{ display: 'flex' }}>
                {showLifelineModules ? (
                  <>
                    <Tooltip title="Cursor 1">
                      <span>
                        <IconButton
                          onClick={() => {
                            eos.cursor = 1;
                          }}
                          disabled={!isVideoConferenceJoined}
                          color="primary"
                          aria-label="cursor-1"
                        >
                          <AdjustIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cursor 2">
                      <span>
                        <IconButton
                          onClick={() => {
                            eos.cursor = 2;
                          }}
                          disabled={!isVideoConferenceJoined}
                          color="primary"
                          aria-label="cursor-2"
                        >
                          <FilterCenterFocusIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Drop Cursor">
                      <span>
                        <IconButton
                          onClick={() => {
                            eos.cursor = 0;
                          }}
                          disabled={!isVideoConferenceJoined}
                          color="primary"
                          aria-label="drop-cursor"
                        >
                          <DoNotDisturbIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Rotate">
                      <span>
                        <IconButton
                          onClick={() => {
                            eos.rotate();
                          }}
                          color="primary"
                          aria-label="rotate"
                        >
                          <Rotate90DegreesCwIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => {
                        eos.cursor = 1;
                      }}
                      disabled={!isVideoConferenceJoined}
                      sx={{ mx: 1, width: '125px' }}
                      startIcon={<AdjustIcon />}
                    >
                      Cursor 1
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        eos.cursor = 2;
                      }}
                      disabled={!isVideoConferenceJoined}
                      sx={{ mr: 1, width: '125px' }}
                      startIcon={<FilterCenterFocusIcon />}
                    >
                      Cursor 2
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        eos.cursor = 0;
                      }}
                      disabled={!isVideoConferenceJoined}
                      sx={{ mr: 1, width: '140px' }}
                      startIcon={<DoNotDisturbIcon />}
                    >
                      Drop Cursor
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        eos.rotate();
                      }}
                      sx={{ width: '125px' }}
                      startIcon={<Rotate90DegreesCwIcon />}
                    >
                      Rotate
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ width: showLifelineModules ? 'auto' : '125px' }}>
              {showLifelineModules && (
                <>
                  <Tooltip title="Expand">
                    <span>
                      <IconButton onClick={handleCloseLifelineModules} color="primary" aria-label="expand">
                        <OpenInFullIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: showLifelineModules ? 'calc(50vw - 145px)' : '0',
              px: 1,
            }}
          >
            {showPatientModule && (
              <AutoCompletePatient
                setFieldValue={(field, value) => setPatientID(value)}
                fieldName={'patient'}
                values={{ patient: patientID }}
                patientID={patientID}
                label={'Search Patient'}
                size="small"
              />
            )}
          </Box>
        </AppBar>

        <SpeedDial
          ariaLabel="Additional actions"
          sx={{ position: 'absolute', bottom: '8px', right: '8px' }}
          FabProps={{
            variant: 'extended',
            size: 'medium',
            sx: {
              borderRadius: '8px', // Rounded corners like a regular button
              backgroundColor: 'primary.main', // Button color
              ':hover': {
                backgroundColor: 'primary.dark', // Color on hover
              },
              height: '36px',
              minWidth: '125px',
            },
          }}
          icon={
            <>
              <SpeedDialIcon /> More
            </>
          } // Text alongside the icon
          // onClose={() => setOpenSpeedDial(false)}
          onClick={() => setOpenSpeedDial(!openSpeedDial)}
          open={openSpeedDial}
        >
          {pageDevice?.type === 'Echo on Sight'
            ? speedDialActionsEoS.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  tooltipOpen
                  onClick={() => {
                    action.action();
                    setOpenSpeedDial(false);
                  }}
                />
              ))
            : speedDialActionsDefault.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  tooltipOpen
                  onClick={() => {
                    action.action();
                    setOpenSpeedDial(false);
                  }}
                />
              ))}
        </SpeedDial>
      </div>
    </>
  );
}
